import { Close as CloseIcon, TuneOutlined as TuneOutlinedIcon } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import is from '@sindresorhus/is';
import { Fragment, useContext, useState } from 'react';
import Maybe from 'true-myth/maybe';

import { hasInitiallyAppliedSectionSettingsCheck } from './helpers';
import { ProfileVisibilityUserSearchField } from './ProfileVisibilityUserSearchField';
import { ResetConfigurationModal } from './ResetConfigurationModal';
import { ApplicationContext } from '../../../../../../shared/source/context/context';
import { RemovableChip } from '../../../../design/components/interactive-chips/RemovableChip';
import { ToggleChip } from '../../../../design/components/interactive-chips/ToggleChip';
import { Headline, SubTitle } from '../../../../design/components/Typography';
import { getUserName } from '../../../common/user';

import type {
    ProfileVisibilityUser,
    SectionVisibilityBase,
    SectionVisibilityResponse,
} from '../../../../../../shared/source/data-contract/profile-visibility';
import type { FunctionComponent } from 'react';

type ProfileSectionAccordionProps = {
    readonly appliedSettings: SectionVisibilityResponse;
    readonly isCheckBoxEnabled?: boolean;
    readonly isSectionDirty: boolean;
    readonly minimumSettings: SectionVisibilityBase;
    readonly onEveryoneClick: () => void;
    readonly onResetSection: (applyToOtherSections: boolean) => void;
    readonly onRoleClick: (role: string) => void;
    readonly onSaveSection: (applyToOtherSections: boolean) => void;
    readonly onUserClick: (user: ProfileVisibilityUser) => void;
    readonly rolesList: readonly string[];
    readonly sectionLabel: string;
    readonly visibilityMessage: string;
};

export const ProfileSectionAccordion: FunctionComponent<ProfileSectionAccordionProps> = ({
    appliedSettings,
    isCheckBoxEnabled = true,
    isSectionDirty,
    minimumSettings,
    onEveryoneClick,
    onResetSection,
    onRoleClick,
    onSaveSection,
    onUserClick,
    rolesList,
    sectionLabel,
    visibilityMessage,
}) => {
    const [openDialog, setOpenDialog] = useState(false);
    const { translate } = useContext(ApplicationContext);
    const { everyone: isEveryoneApplied, roles: appliedRoles, users: appliedUsers } = appliedSettings;
    const [isAppliedToAll, setIsAppliedToAll] = useState(false);

    const setIsAppliedToAllAction = (value: boolean): void => {
        isCheckBoxEnabled && setIsAppliedToAll(value);
    };

    const hasInitialSettings = hasInitiallyAppliedSectionSettingsCheck(appliedSettings, minimumSettings);

    const displayUserSearch = !isEveryoneApplied;

    return (
        <Grid aria-label="Profile Section Accordion" mx={3} my={1}>
            <ResetConfigurationModal
                closeModal={() => setOpenDialog(false)}
                content={translate('pages.user-profile.profile-visibility.drawer.reset-configuration.section.content')}
                open={openDialog}
                title={translate('pages.user-profile.profile-visibility.drawer.reset-configuration.section.title')}
                onReset={() => {
                    setIsAppliedToAllAction(false);
                    onResetSection(isAppliedToAll);
                }}
            />
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#F6F6F6' }}>
                    <Grid
                        alignItems="center"
                        display="grid"
                        gap={3}
                        gridTemplateColumns="minmax(min-content, 200px) auto"
                    >
                        <Box sx={{ whiteSpace: 'nowrap' }}>
                            <Headline color="text.primary" variant="h5">
                                {sectionLabel}
                            </Headline>
                        </Box>
                        <SubTitle>{visibilityMessage}</SubTitle>
                    </Grid>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid
                        alignItems="center"
                        display="grid"
                        gap={3}
                        gridTemplateColumns="auto 1fr"
                        gridTemplateRows={displayUserSearch ? '1fr 1fr auto' : '1fr'}
                        padding={2}
                    >
                        <Box>
                            {translate('pages.user-profile.profile-visibility.drawer.sections.visible-roles-label')}
                        </Box>
                        <Grid container={true} gap={1}>
                            {rolesList.map((role) => {
                                const isActive = appliedRoles.includes(role);
                                const isMandatory = minimumSettings.roles.includes(role);

                                return (
                                    <ToggleChip
                                        key={role}
                                        disabled={minimumSettings.everyone || (isMandatory && isActive)}
                                        isActive={isActive}
                                        label={translate(`roles.${role}.label`)}
                                        onClick={() => onRoleClick(role)}
                                    />
                                );
                            })}
                            <ToggleChip
                                isActive={isEveryoneApplied}
                                label={translate('roles.all.label')}
                                onClick={onEveryoneClick}
                            />
                        </Grid>
                        {displayUserSearch && (
                            <Fragment>
                                <Box>
                                    {translate(
                                        'pages.user-profile.profile-visibility.drawer.sections.visible-people-label',
                                    )}
                                </Box>
                                <Grid width="100%">
                                    <ProfileVisibilityUserSearchField
                                        addToList={onUserClick}
                                        appliedUsers={appliedUsers}
                                    />
                                </Grid>
                                <Grid container={true} gap={1} gridColumn={2} gridRow={3}>
                                    {appliedUsers.map((user) => (
                                        <RemovableChip
                                            key={user.id}
                                            isActive={true}
                                            label={getUserName(Maybe.of(user))}
                                            onDelete={() => onUserClick(user)}
                                        />
                                    ))}
                                </Grid>
                            </Fragment>
                        )}
                    </Grid>
                    <Grid alignItems="center" display="flex" justifyContent="flex-end" margin={3}>
                        {isCheckBoxEnabled && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isAppliedToAll}
                                        onChange={() => setIsAppliedToAllAction(!isAppliedToAll)}
                                    />
                                }
                                label={translate(
                                    'pages.user-profile.profile-visibility.drawer.apply-settings-to.apply-to-all-label',
                                )}
                            />
                        )}
                        <Grid alignItems="center" display="flex" gap={1}>
                            <Button
                                disabled={is.falsy(isAppliedToAll) && is.falsy(isSectionDirty)}
                                startIcon={<TuneOutlinedIcon />}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'primary.main',
                                    },
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText',
                                }}
                                type="button"
                                variant="contained"
                                onClick={() => {
                                    setIsAppliedToAllAction(false);
                                    onSaveSection(isAppliedToAll);
                                }}
                            >
                                {translate(
                                    'pages.user-profile.profile-visibility.drawer.apply-settings-to.apply-button',
                                )}
                            </Button>
                            <Button
                                aria-label="Reset Entity Button"
                                disabled={is.truthy(hasInitialSettings)}
                                startIcon={<CloseIcon />}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'secondary.main',
                                    },
                                    backgroundColor: 'secondary.main',
                                    color: 'secondary.contrastText',
                                }}
                                type="button"
                                variant="contained"
                                onClick={() => setOpenDialog(true)}
                            >
                                {translate(
                                    'pages.user-profile.profile-visibility.drawer.apply-settings-to.reset-button',
                                )}
                            </Button>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Grid>
    );
};
